import type { Role } from '@/stores/useSessionStore'
import { hasInstrumentCreateContext } from '@/features/instruments/lib/instrumentCreateSession'
import { readInstrumentWorkspaceContext } from '@/features/instruments/lib/instrumentWorkspaceSession'

/**
 * Sidebar “sections” for active highlight + click / no-op rules.
 *
 * Extend `resolveActiveSection` when new routes belong to Campaigns context
 * (e.g. response analytics under `/campaigns/:id/...`) so the nav stays correct
 * without changing AppShell JSX.
 */
export type NavSection =
  | 'dashboard'
  | 'campaigns'
  | 'companies'
  | 'reports'
  | 'library'
  | 'users'
  | 'tenants'
  | 'signalProviders'
  | 'notificationManagement'
  | 'platformTemplateLibrary'

const TEAM_AND_LAUNCH = /^\/campaigns\/[^/]+\/team-and-launch\/?$/
const INSTRUMENT_BUILDER = /^\/instruments\/[^/]+\/builder\/?$/

export function isTeamAndLaunchPath(pathname: string): boolean {
  return TEAM_AND_LAUNCH.test(pathname)
}

function hasCampaignInstrumentQuery(search: string): boolean {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const campaignId = q.get('campaignId') ?? ''
  const loiId = q.get('loiId') ?? ''
  return Boolean(campaignId && loiId)
}

const INSTRUMENT_EDIT_INFO = /^\/instruments\/[^/]+\/info\/?$/

/** Instrument flows that are part of the Campaigns journey (BFP-289). */
export function isInstrumentCampaignContext(pathname: string, search: string): boolean {
  if (
    pathname === '/instruments/create' ||
    pathname === '/instruments/create/templates' ||
    pathname === '/instruments/create/info'
  ) {
    return hasInstrumentCreateContext() || hasCampaignInstrumentQuery(search)
  }
  if (INSTRUMENT_EDIT_INFO.test(pathname) && !pathname.startsWith('/instruments/create/')) {
    return true
  }
  if (INSTRUMENT_BUILDER.test(pathname)) {
    const match = INSTRUMENT_BUILDER.exec(pathname)
    const instrumentId = match?.[1] ?? ''
    if (instrumentId && readInstrumentWorkspaceContext(instrumentId)?.campaignId) {
      return true
    }
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    return Boolean(q.get('campaignId'))
  }
  return false
}

export function resolveActiveSection(pathname: string, search: string): NavSection | null {
  if (pathname.startsWith('/users')) {
    return 'users'
  }
  if (pathname.startsWith('/tenants')) {
    return 'tenants'
  }
  if (pathname.startsWith('/signal-providers')) {
    return 'signalProviders'
  }
  if (pathname.startsWith('/notification-management')) {
    return 'notificationManagement'
  }
  if (pathname.startsWith('/platform/template-library')) {
    return 'platformTemplateLibrary'
  }
  if (pathname === '/') {
    return 'dashboard'
  }
  if (pathname.startsWith('/companies')) {
    return 'companies'
  }
  if (pathname.startsWith('/campaigns')) {
    return 'campaigns'
  }
  if (pathname === '/reports' || pathname.startsWith('/reports/')) {
    return 'reports'
  }
  if (
    pathname.startsWith('/response-dashboard/') ||
    pathname.startsWith('/loi-dashboard/') ||
    pathname.startsWith('/instrument-dashboard/') ||
    pathname.startsWith('/question-dashboard/')
  ) {
    return 'campaigns'
  }
  if (isInstrumentCampaignContext(pathname, search)) {
    return 'campaigns'
  }
  if (pathname === '/library' || pathname.startsWith('/library/')) {
    return 'library'
  }
  return null
}

export type NavClickResult = { action: 'noop' } | { action: 'navigate'; to: string }

/** Campaign landing list — `/campaigns` only (not nested campaign routes). */
export function isCampaignLandingPath(pathname: string): boolean {
  return pathname === '/campaigns' || pathname === '/campaigns/'
}

/**
 * BFP-289: same-section click is a no-op on top-level destinations (e.g. `/library`).
 * Campaigns is an exception: from any nested campaign journey (wizard, detail,
 * response dashboards, instrument builder in campaign context), clicking Campaigns
 * navigates up to the campaign list.
 */
export function resolveNavClick(
  itemSection: NavSection,
  itemPath: string,
  pathname: string,
  search: string,
): NavClickResult {
  const active = resolveActiveSection(pathname, search)
  if (itemSection !== active) {
    return { action: 'navigate', to: itemPath }
  }
  if (
    itemSection === 'campaigns' &&
    active === 'campaigns' &&
    !isCampaignLandingPath(pathname)
  ) {
    return { action: 'navigate', to: '/campaigns' }
  }
  return { action: 'noop' }
}

export function formatSensemakerRoleLabel(role: Role | undefined): string {
  if (!role) {
    return 'Sense Maker'
  }
  switch (role) {
    case 'ADMIN':
      return 'Sense Maker Admin'
    case 'EDITOR':
      return 'Sense Maker Editor'
    case 'VIEWER':
      return 'Sense Maker Viewer'
    case 'PLATFORM_ADMIN':
      return 'Platform Admin'
    default:
      return 'Sense Maker'
  }
}
